import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { platformSettings } from '@/api/platformSettings';
import { PageHeader } from '@/components/PageHeader';
import { DataTable, type Column } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

type Row = {
  id: string; name: string; email: string; message: string;
  handled_at: string | null; created_at: string;
};

export function ContactSubmissions() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['contactSubmissions'],
    queryFn: () => platformSettings.listContactSubmissions({}),
  });
  const mark = useMutation({
    mutationFn: (id: string) => platformSettings.markContactHandled(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contactSubmissions'] }),
  });

  const columns: Column<Row>[] = [
    {
      header: 'When',
      cell: (r) => <span className="tabular-nums text-muted-foreground">{new Date(r.created_at).toLocaleString()}</span>,
      headClassName: 'whitespace-nowrap',
    },
    { header: 'Name', cell: (r) => <span className="font-medium">{r.name}</span> },
    { header: 'Email', cell: (r) => <span className="break-all">{r.email}</span> },
    {
      header: 'Message',
      cell: (r) => <span className="line-clamp-2 max-w-md text-sm">{r.message}</span>,
    },
    {
      header: 'Status',
      cell: (r) => r.handled_at
        ? <Badge variant="secondary">Handled</Badge>
        : (
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => { e.stopPropagation(); mark.mutate(r.id); }}
          >
            Mark handled
          </Button>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Settings"
        title="Contact submissions"
        description="Messages sent via the public contact form on the landing page."
      />
      {isLoading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : error ? (
        <div className="text-sm text-destructive">{(error as Error).message}</div>
      ) : (
        <DataTable
          rows={(data?.rows ?? []) as Row[]}
          columns={columns}
          empty="No submissions yet."
        />
      )}
    </>
  );
}
